<template>
  <div v-qa="'calculator'" :class="{ disabled, wide, full, editable }" class="calculator">
    <calculator-tabs :limit="limit"/>

    <div v-if="!unfinishedRegistrationAndHasApplication" class="calculator__top sf-ph-s sf-pv-s">
      <sf-calculator
        ref="sliders"
        class="calculator__sliders"
        v-bind="{ mode, limit, colors }"
        @edit="updateEdit">
        <template slot="edit-loan">{{ $t('calculator.edit-loan') }}</template>
        <template slot="loan-amount">{{ $t('calculator.loan-amount') }}</template>
        <template slot="loan-days">{{ $t('calculator.loan-days') }}</template>
        <template slot="days-select">{{ $t('calculator.days') }}</template>
      </sf-calculator>

      <sf-slide-toggle :value="aboveAvailableAmount" class="calculator__tooltip">
        <div class="sf-mt-s sf-ta-c">
          <nuxt-link :to="localePath('login')">{{ $t('calculator.repeated-user') }}</nuxt-link>
        </div>
      </sf-slide-toggle>

      <div class="calculator__button">
        <slot name="button">
          <sf-button
            v-if="isButtonVisible"
            v-qa="'btn_calculator_submit'"
            :disabled="disabled"
            :url="url"
            class="block secondary">
            <template v-if="aboveAvailableAmount">{{ $t('calculator.log-in') }}</template>
            <template v-else>{{ $t('calculator.submit') }}</template>
          </sf-button>
        </slot>
      </div>
    </div>

    <div class="calculator__bottom">
      <calculator-stats
        :with-insurance="withInsurance"
        :is-application-info="unfinishedRegistrationAndHasApplication"/>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { WEB_ROUTES } from '~/utils/webConfig';
import CalculatorStats from '~/components/calculator/stats.vue';
import CalculatorTabs from '~/components/calculator/tabs.vue';

export default {
  components: { CalculatorTabs, CalculatorStats },
  props: {
    mode: {
      type: String,
      default: 'full', // full || edit
    },
    limit: {
      type: String,
      default: '',
    },
    wide: {
      type: Boolean,
      default: false,
    },
    withInsurance: {
      type: Boolean,
      default: false,
    },
    withButton: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      editable: this.mode === 'full',
      colors: {
        process: this.$theme.colors.secondary,
        arrows: this.$theme.colors.secondary,
        dot: this.$theme.colors.white,
        processAlt: this.$theme.colors.secondary,
        arrowsAlt: this.$theme.colors.secondary,
        // dotAlt: this.$theme.colors.white,
      },
      newClientInterestDiscount: 0,
      repeatedTotal: 0,
      repeatedInterest: 0,
    };
  },
  computed: {
    ...mapGetters('arithmetic', [
      'amount',
      'disabled',
      'authorized',
      'first',
      'repeated',
      'displayed',
      'term',
    ]),
    ...mapGetters('auth', ['isClient']),
    registrationSteps() {
      const { name } = this.$route;
      return ['registration-step2', 'registration-step3', 'registration-step4'].includes(
        `${name}`.split('___')[0]
      );
    },
    unfinishedRegistrationAndHasApplication() {
      return Boolean(this.registrationSteps && this.$client.profile.hasUncancelledApplications);
    },
    aboveAvailableAmount() {
      if (this.disabled || this.authorized) return false;
      return this.amount.current > this.amount.first;
    },
    tab() {
      return this.$store.state.arithmetic.current.tab;
    },
    sliderTerm() {
      return this.$store.state.arithmetic.current.term;
    },
    sliderAmount() {
      return this.$store.state.arithmetic.current.amount;
    },
    full() {
      return this.mode === 'full';
    },
    url() {
      if (this.authorized) {
        return this.localePath(WEB_ROUTES.APPLY);
      }

      if (this.amount.current > this.amount.first) {
        return this.localePath(WEB_ROUTES.LOGIN);
      }

      return this.localePath(WEB_ROUTES.REGISTRATION_STEP_1);
    },
    isButtonVisible() {
      return this.withButton && this.full;
    },
  },
  methods: {
    updateEdit(editable) {
      this.editable = !!editable;
    },
    refresh() {
      this.$refs.sliders.refresh();
    },
  },
};
</script>

<style lang="scss" scoped>
.calculator {
  position: relative;
  width: 100%;
  background: map-get($colors, primary);
  outline: none;
  overflow: hidden;
  box-shadow: -2px 4px 20px rgba(#000, 0.15);
  color: map-get($colors, white);

  @include tablet {
    border-radius: 10px;
    width: 375px;
  }

  &.wide {
    @include not-desktop {
      width: 100%;
    }
  }

  &.disabled {
    &:after {
      @extend %overlay;
      position: absolute;
      z-index: 1;
      background: rgba(#fff, 0.5);
      content: '';

      @include tablet {
        border-radius: 10px;
      }
    }
  }

  &.full {
    .calculator__top {
      padding-bottom: map-get($paddings, normal);
    }

    .calculator__stats {
      padding-top: 45px;
    }
  }

  &:not(.full) {
    .calculator__tooltip {
      display: none;
    }
  }

  &:not(.editable) {
    @include mobile {
      .calculator__stats {
        display: none;
      }
    }
  }

  &__top {
    position: relative;
  }

  &__tabs {
    color: #fff;
  }

  &__bottom {
    background: map-get($colors, white);
    color: map-get($colors, gray);
  }

  &__tooltip a {
    color: inherit;
  }

  &__button {
    position: absolute;
    bottom: 0;
    z-index: 5;
    transform: translateY(50%);

    @include mobile {
      left: map-get($paddings, smallM);
      right: map-get($paddings, smallM);
    }

    @include tablet {
      left: map-get($paddings, small);
      right: map-get($paddings, small);
    }
  }

  /deep/ .edit {
    padding: 0;
  }
}
</style>
